import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import CreateTask from "../Task/Create";
import StatusUpdate from "../Task/StatusUpdate";

// CSS for the Kanban board
const kanbanBoardStyles = `
  .kanban-board {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: calc(100vh - 220px);
    overflow: auto;
  }

  .kanban-headers {
    display: flex;
    position: sticky;
    top: 0;
    z-index: 10;
    margin-bottom: 8px;
  }

  .kanban-header {
    flex: 1;
    padding: 12px 16px;
    font-weight: 600;
    border-radius: 4px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    margin: 0 8px;
  }

  .story-row {
    display: flex;
    width: 100%;
    margin-bottom: 5px;
  }

  .story-header {
    padding: 12px;
    background-color:rgb(242, 243, 244);
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    border-left: 3px solid #0747A6;
    margin-bottom: 8px;
  }

  .task-cells-container {
    display: flex;
    width: 100%;
  }

  .task-cell {
    flex: 1;
    padding: 8px;
    min-height: 50px;
    margin: 0 8px;
    background-color: #f8f9fa;
    border-radius: 4px;
  }

  .task-card {
    margin: 8px 0;
    padding: 12px;
    background: white;
    border-radius: 4px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    border-left: 3px solid #4caf50;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    user-select: none;
    cursor: grab;
  }

  .task-card:hover {
    box-shadow: 0 3px 6px rgba(0,0,0,0.15);
  }
  
  .task-card.dragging {
    transform: scale(1.02);
    box-shadow: 0 5px 10px rgba(0,0,0,0.2);
    opacity: 0.9;
    cursor: grabbing;
  }

  .task-cell.drag-over {
    background-color: rgba(13, 110, 253, 0.05);
    box-shadow: inset 0 0 0 2px rgba(13, 110, 253, 0.3);
  }

  .status-badge {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    color: white;
  }

  .avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: #0d6efd;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
  }

  .section-header {
    font-weight: 600;
    font-size: 14px;
    margin: 8px 0;
    padding: 8px;
    background-color: #f5f5f5;
    border-radius: 4px;
  }

  .standalone-container {
    margin-top: 16px;
  }
  
  /* Custom styling for minimal UI */
  .board-filters {
    display: flex;
    // align-items: center;
    gap: 8px;
    width: 100%;
    margin-top: 8px;
    height: 40px;
  }
  
  .board-filters .p-dropdown,
  .board-filters .p-inputtext {
    height: 36px;
    font-size: 0.875rem;
  }
  
  .board-filters .p-dropdown {
    min-width: 150px;
  }
  
  .board-filters .p-button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
  
  .board-filters .filter-clear:hover {
    color: #dc3545;
  }
  
  .board-filters .filter-refresh:hover {
    color: #0d6efd;
  }
  
  .board-filters .p-dropdown .p-dropdown-label {
    padding-top: 6px;
    padding-bottom: 6px;
  }
  
  .filter-alert {
    margin-top: 10px;
    padding: 6px 12px;
    border-radius: 4px;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

const List = () => {
  // References
  const toast = useRef(null);

  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [projectFilter, setProjectFilter] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [sprintFilter, setSprintFilter] = useState(null);
  const [sprintOptions, setSprintOptions] = useState([]);
  const [developerFilter, setDeveloperFilter] = useState(null);
  const [developerOptions, setDeveloperOptions] = useState([]);

  // Current user state
  const [currentUser, setCurrentUser] = useState(null);

  // Data states
  const [stories, setStories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [expandedStories, setExpandedStories] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  // Dialog visibility states
  const [statusUpdateVisible, setStatusUpdateVisible] = useState(false);
  const [createTaskVisible, setCreateTaskVisible] = useState(false);

  // Selection states
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);

  // Mapping data
  const [projects, setProjects] = useState({});
  const [projectOptions, setProjectOptions] = useState([]);
  const [labels, setLabels] = useState({});
  const [developers, setDevelopers] = useState({});
  const [taskTypes, setTaskTypes] = useState({});

  // Status constants
  const statuses = ["To Do", "In Progress", "Review", "Done"];
  const statusColors = {
    "To Do": "#6c757d", // gray
    "In Progress": "#0d6efd", // blue
    Pause: "#dc3545", // red
    Review: "#fd7e14", // orange
    Done: "#198754", // green
  };
  const statusBackgrounds = {
    "To Do": "#f0f0f0", // light gray
    "In Progress": "#e6f0ff", // light blue
    Pause: "#ffebee", // light red
    Review: "#fff3e0", // light orange
    Done: "#e6f3e6", // light green
  };

  // Task status cache to avoid repeated API calls
  const [taskStatusHistory, setTaskStatusHistory] = useState({});

  // Function to check if a status transition is valid
  const isValidStatusTransition = (currentStatus, newStatus) => {
    // Special handling for Pause status
    if (newStatus === "Pause") {
      // Can only pause if task is in In Progress or Review, not To Do
      return currentStatus === "In Progress" || currentStatus === "Review";
    }

    // Special handling for returning from Pause
    if (currentStatus === "Pause") {
      // Can resume to In Progress or Review
      return newStatus === "Start" || newStatus === "In Progress" || newStatus === "Review" || newStatus === "Done";
    }

    // For regular forward progression
    const statusOrder = ["To Do", "In Progress", "Review", "Done"];
    const currentIndex = statusOrder.indexOf(currentStatus);
    const newIndex = statusOrder.indexOf(newStatus);

    // Only allow moving forward in the workflow (skipping Pause in the regular flow)
    return newIndex > currentIndex;
  };

  // Function to check if current user is assigned to a task
  const isUserAssignedToTask = (task) => {
    if (!currentUser || !task || !task.developers) return false;

    // Check if the current user is among the assigned developers
    return task.developers.some((dev) => dev.id === currentUser.id);
  };

  // Dragging state
  const [draggingTask, setDraggingTask] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);

  // State to track highlighted items (newly created or edited)
  const [highlightedItems, setHighlightedItems] = useState({});

  // Handle drag events
  const handleDragStart = (e, task) => {
    // Prevent dragging if task is paused
    if (task.status === "Pause") {
      e.preventDefault();
      e.stopPropagation();
      toast.current.show({
        severity: "info",
        summary: "Paused Task",
        detail: "Paused tasks must be resumed first before changing status",
        life: 3000,
      });
      return false;
    }

    // Prevent dragging if user is not assigned to this task
    if (!isUserAssignedToTask(task)) {
      e.preventDefault();
      e.stopPropagation();
      toast.current.show({
        severity: "error",
        summary: "Permission Denied",
        detail: "You can only change status of tasks assigned to you",
        life: 3000,
      });
      return false;
    }

    setDraggingTask(task);
    // Add a class to the dragged element
    e.currentTarget.classList.add("dragging");
  };

  const handleDragEnd = (e) => {
    // Remove the dragging class
    e.currentTarget.classList.remove("dragging");
    setDraggingTask(null);
    setDragOverCell(null);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    setDragOverCell(status);
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();

    if (draggingTask && draggingTask.status !== newStatus) {
      // Check if the user is assigned to the task
      if (!isUserAssignedToTask(draggingTask)) {
        toast.current.show({
          severity: "error",
          summary: "Permission Denied",
          detail: "You can only change status of tasks assigned to you",
          life: 3000,
        });
        setDraggingTask(null);
        setDragOverCell(null);
        return;
      }

      // Check if the transition is valid (only forward movement)
      if (!isValidStatusTransition(draggingTask.status, newStatus)) {
        toast.current.show({
          severity: "error",
          summary: "Invalid Transition",
          detail: "Tasks can only be moved forward in the workflow",
          life: 3000,
        });
      } else {
        // Update the task status
        handleStatusChange(draggingTask.id, newStatus);
      }
    }

    setDraggingTask(null);
    setDragOverCell(null);
  };

  // Function to reset all filters
  const resetAllFilters = () => {
    setSearchTerm("");
    setProjectFilter(null);
    setSprintFilter(null);
    setDeveloperFilter(null);
  };

  // Fetch all necessary data
  useEffect(() => {
    fetchData();
    fetchCurrentUser();
  }, []);

  // Fetch current logged in user
  const fetchCurrentUser = async () => {
    try {
      const response = await Http.get("/me");
      if (response.data && response.data.data) {
        setCurrentUser(response.data.data);
        console.log("Current user data:", response.data.data);
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      // Fetch sprints first to get the active sprint
      const sprintsResponse = await Http.get("/sprint");
      setSprints(sprintsResponse.data.data || []);

      // Create sprint options for dropdown
      const options = [{ label: "All Sprints", value: null }];
      const sprintsData = sprintsResponse.data.data || [];
      let activeSprint = null;

      sprintsData.forEach((sprint) => {
        options.push({ label: sprint.name, value: sprint.id });
        if (sprint.status === "Active") {
          activeSprint = sprint;
        }
      });

      setSprintOptions(options);

      // Set active sprint as default if available
      if (activeSprint) {
        setSprintFilter(activeSprint.id);
      }

      // Fetch stories and other data
      let tasksEndpoint = activeSprint ? `/sprint/${activeSprint.id}/tasks` : "/task";
      const [storiesResponse, tasksResponse] = await Promise.all([Http.get("/story"), Http.get(tasksEndpoint)]);

      // Set stories and tasks
      const fetchedStories = storiesResponse.data.data || [];
      const fetchedTasks = tasksResponse.data.data || [];

      setStories(fetchedStories);
      setTasks(fetchedTasks);

      // Fetch related data for displaying names instead of IDs
      await Promise.allSettled([fetchProjects(), fetchLabels(), fetchDevelopers(), fetchTaskTypes()]);

      // Fetch status history for paused tasks
      await fetchPausedTasksHistory();

      // Initialize expanded state for stories: Collapse if 100% done initially
      const initialExpandedState = {};
      fetchedStories.forEach((story) => {
        // Calculate progress using the *fetched* tasks, not the state variable which might not be updated yet
        const storyTasks = fetchedTasks.filter((task) => task.story_id === story.id);
        const doneCount = storyTasks.filter((task) => task.status === "Done").length;
        const totalCount = storyTasks.length;
        const isComplete = totalCount > 0 && doneCount === totalCount;

        initialExpandedState[story.id] = !isComplete; // Start collapsed if complete, otherwise expanded
      });
      setExpandedStories(initialExpandedState);
    } catch (error) {
      console.error("Error fetching board data:", error);
      setError("Failed to load board data. Please refresh the page to try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects
  const fetchProjects = async () => {
    try {
      const res = await Http.get(`/project`);
      const projectsMap = {};
      const options = [{ label: "All Projects", value: null }];

      if (res.data && res.data.data) {
        res.data.data.forEach((project) => {
          projectsMap[project.id] = project.title;
          options.push({ label: project.title, value: project.id });
        });
      }
      setProjects(projectsMap);
      setProjectOptions(options);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  // Fetch labels
  const fetchLabels = async () => {
    try {
      const res = await Http.get(`/label`);
      const labelsMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((label) => {
          labelsMap[label.id] = {
            name: label.name,
            color: label.color,
          };
        });
      }
      setLabels(labelsMap);
    } catch (error) {
      console.error("Error fetching labels:", error);
    }
  };

  // Fetch developers
  const fetchDevelopers = async () => {
    try {
      const res = await Http.get(`/staff-department-wise/30`);
      const developersMap = {};
      // All developers option should not be selectable
      const options = [{ label: "All Developers", value: null, disabled: true }];

      if (Array.isArray(res.data)) {
        res.data.forEach((dev) => {
          const fullName = `${dev.first_name || ""} ${dev.last_name || ""}`.trim();
          const displayName = fullName || dev.name || `ID: ${dev.id}`;

          developersMap[dev.id] = displayName;

          // Add to dropdown options
          options.push({
            label: displayName,
            value: dev.id,
            // Optional: add avatar data if needed
            initials: displayName
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .substring(0, 2),
          });
        });
      }

      setDevelopers(developersMap);
      setDeveloperOptions(options);
    } catch (error) {
      console.error("Error fetching developers:", error);
    }
  };

  // Fetch task types
  const fetchTaskTypes = async () => {
    try {
      const res = await Http.get(`/task-type`);
      const taskTypesMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((type) => {
          taskTypesMap[type.id] = {
            name: type.task_type,
            icon: type.icon || "pi pi-check-square",
            color: type.color || "#4CAF50",
          };
        });
      }
      setTaskTypes(taskTypesMap);
    } catch (error) {
      console.error("Error fetching task types:", error);
    }
  };

  // Toggle story expansion
  const toggleStoryExpansion = (storyId) => {
    setExpandedStories((prev) => ({
      ...prev,
      [storyId]: !prev[storyId],
    }));
  };

  // Toggle description expansion
  const toggleDescriptionExpansion = (taskId, e) => {
    e.stopPropagation();
    setExpandedDescriptions((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  // Handle sprint filter change
  const handleSprintChange = async (selectedSprintId) => {
    setSprintFilter(selectedSprintId);
    setLoading(true);

    try {
      // Fetch tasks for selected sprint or all tasks if no sprint selected
      const tasksEndpoint = selectedSprintId ? `/sprint/${selectedSprintId}/tasks` : "/task";
      const tasksResponse = await Http.get(tasksEndpoint);
      setTasks(tasksResponse.data.data || []);

      // Fetch status history for paused tasks after tasks are updated
      await fetchPausedTasksHistory();
    } catch (error) {
      console.error("Error fetching tasks for sprint:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load tasks for the selected sprint",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter stories and tasks based on search term, project filter, and sprint filter
  const filteredStories = stories.filter((story) => {
    // Apply search and project filters
    const matchesSearchAndProject =
      (story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (projects[story.project_id] && projects[story.project_id].toLowerCase().includes(searchTerm.toLowerCase()))) &&
      (!projectFilter || story.project_id === projectFilter);

    // If sprint filter is active, only include stories that have tasks in this sprint
    if (sprintFilter || developerFilter) {
      // Get all tasks for this story
      const storyTasks = tasks.filter((task) => task.story_id === story.id);

      // Filter by sprint if needed
      const tasksMatchingSprint = sprintFilter ? storyTasks.filter((task) => task.sprint_id === sprintFilter) : storyTasks;

      // Filter by developer if needed
      const tasksMatchingDeveloper = developerFilter
        ? tasksMatchingSprint.filter((task) => task.developers && task.developers.some((dev) => dev.id === developerFilter))
        : tasksMatchingSprint;

      // Include story if it has any tasks that match all active filters
      return matchesSearchAndProject && tasksMatchingDeveloper.length > 0;
    }

    return matchesSearchAndProject;
  });

  const filteredTasks = tasks.filter(
    (task) =>
      (task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.project?.title && task.project.title.toLowerCase().includes(searchTerm.toLowerCase()))) &&
      (!projectFilter || task.project_id === projectFilter) &&
      (!sprintFilter || task.sprint_id === sprintFilter) &&
      (!developerFilter || (task.developers && task.developers.some((dev) => dev.id === developerFilter)))
  );

  // Get tasks for a specific story
  const getTasksForStory = (storyId) => {
    return filteredTasks.filter((task) => task.story_id === storyId);
  };

  // Get standalone tasks (not part of any story)
  const getStandaloneTasks = () => {
    return filteredTasks.filter((task) => !task.story_id);
  };

  // Get tasks by status
  const getTasksByStatus = (tasks, status) => {
    // First, get all tasks that directly have this status
    const directStatusTasks = tasks.filter((task) => task.status === status);

    // Next, find paused tasks that were previously in this status
    const pausedTasks = tasks.filter((task) => task.status === "Pause" && getTaskPreviousStatus(task) === status);

    // Combine both sets of tasks
    return [...directStatusTasks, ...pausedTasks];
  };

  // Helper function to get the previous status of a paused task
  const getTaskPreviousStatus = (task) => {
    // If we already have the status history cached, use it
    if (taskStatusHistory[task.id]) {
      return taskStatusHistory[task.id];
    }

    // Default to "In Progress" if we can't determine
    return "In Progress";
  };

  // Function to fetch status history for paused tasks
  const fetchPausedTasksHistory = async () => {
    try {
      const pausedTasks = tasks.filter((task) => task.status === "Pause");

      if (pausedTasks.length === 0) return;

      // Create a new history cache
      const newHistoryCache = { ...taskStatusHistory };

      // Fetch history for each paused task that we don't already have cached
      for (const task of pausedTasks) {
        if (!taskStatusHistory[task.id]) {
          try {
            const response = await Http.get(`/task/${task.id}`);
            if (response.data && response.data.data && response.data.data.status_history) {
              const history = response.data.data.status_history;
              // Find the most recent Pause entry
              const pauseEntry = history.find((entry) => entry.new_status === "Pause");

              if (pauseEntry && pauseEntry.old_status) {
                // Store the previous status
                newHistoryCache[task.id] = pauseEntry.old_status;
              } else if (pauseEntry && pauseEntry.comment) {
                // Try to extract status from comment
                const match = pauseEntry.comment.match(/Task paused from (.*)/);
                if (match && match[1]) {
                  newHistoryCache[task.id] = match[1].trim();
                } else {
                  newHistoryCache[task.id] = "In Progress"; // Default
                }
              } else {
                newHistoryCache[task.id] = "In Progress"; // Default
              }
            }
          } catch (error) {
            console.error(`Error fetching history for task ${task.id}:`, error);
            newHistoryCache[task.id] = "In Progress"; // Default on error
          }
        }
      }

      // Update the cache state
      setTaskStatusHistory(newHistoryCache);
    } catch (error) {
      console.error("Error fetching paused tasks history:", error);
    }
  };

  // Handle status change
  const handleStatusChange = async (taskId, newStatus) => {
    try {
      // Get the task first
      const task = tasks.find((t) => t.id === taskId);

      // Check if the user is assigned to the task
      if (!isUserAssignedToTask(task)) {
        toast.current.show({
          severity: "error",
          summary: "Permission Denied",
          detail: "You can only change status of tasks assigned to you",
          life: 3000,
        });
        return;
      }

      // Special handling for tasks with "Start" action (resuming from Pause)
      if (task.status === "Pause" && newStatus === "Start") {
        // Get the previous status from our cache or fetch it
        let previousStatus = getTaskPreviousStatus(task);

        // If we don't have a cached status, fetch the task details to check history
        if (!taskStatusHistory[task.id]) {
          try {
            const response = await Http.get(`/task/${task.id}`);
            if (response.data && response.data.data && response.data.data.status_history) {
              const history = response.data.data.status_history;
              // Find the most recent Pause entry
              const pauseEntry = history.find((entry) => entry.new_status === "Pause");

              if (pauseEntry && pauseEntry.old_status) {
                previousStatus = pauseEntry.old_status;
              } else if (pauseEntry && pauseEntry.comment) {
                // Try to extract status from comment
                const match = pauseEntry.comment.match(/Task paused from (.*)/);
                if (match && match[1]) {
                  previousStatus = match[1].trim();
                }
              }

              // Update the cache
              setTaskStatusHistory((prev) => ({
                ...prev,
                [task.id]: previousStatus,
              }));
            }
          } catch (error) {
            console.error(`Error fetching history for task ${task.id}:`, error);
          }
        }

        // Auto-pause any active tasks if we're resuming to In Progress or Review
        if ((previousStatus === "In Progress" || previousStatus === "Review") && currentUser) {
          // Find other tasks that are In Progress or Review and assigned to the current user
          const otherActiveTasks = tasks.filter(
            (t) => t.id !== taskId && (t.status === "In Progress" || t.status === "Review") && isUserAssignedToTask(t)
          );

          // If there are other active tasks, pause them automatically
          if (otherActiveTasks.length > 0) {
            toast.current.show({
              severity: "info",
              summary: "Auto-Pause",
              detail: `Automatically pausing ${otherActiveTasks.length} other active task(s)`,
              life: 3000,
            });

            // Pause each task one by one
            for (const taskToPause of otherActiveTasks) {
              const pausePayload = {
                status: "Pause",
                comment: `Task auto-paused from ${taskToPause.status} when resuming task #${taskId}`,
                previous_status: taskToPause.status,
              };

              await Http.patch(`/task/${taskToPause.id}/status`, pausePayload);

              // Update local state and cache the previous status
              setTasks((prevTasks) => prevTasks.map((t) => (t.id === taskToPause.id ? { ...t, status: "Pause" } : t)));

              setTaskStatusHistory((prev) => ({
                ...prev,
                [taskToPause.id]: taskToPause.status,
              }));
            }
          }
        }

        // Now resume to the previous status
        await Http.patch(`/task/${task.id}/status`, {
          status: previousStatus,
          comment: `Task resumed to ${previousStatus} from Pause`,
        });

        // Update task status locally
        setTasks((prevTasks) => prevTasks.map((t) => (t.id === taskId ? { ...t, status: previousStatus } : t)));

        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: `Task resumed to ${previousStatus} status`,
          life: 3000,
        });

        return;
      }

      // Auto-pause active tasks when starting a new one
      if (newStatus === "In Progress" && currentUser) {
        // Find other tasks that are In Progress and assigned to the current user
        const otherInProgressTasks = tasks.filter(
          (t) => t.id !== taskId && (t.status === "In Progress" || t.status === "Review") && isUserAssignedToTask(t)
        );

        // If there are other tasks in progress, pause them automatically
        if (otherInProgressTasks.length > 0) {
          toast.current.show({
            severity: "info",
            summary: "Auto-Pause",
            detail: `Automatically pausing ${otherInProgressTasks.length} other active task(s)`,
            life: 3000,
          });

          // Pause each task one by one
          for (const taskToPause of otherInProgressTasks) {
            const pausePayload = {
              status: "Pause",
              comment: `Task auto-paused from ${taskToPause.status} when starting task #${taskId}`,
              previous_status: taskToPause.status,
            };

            await Http.patch(`/task/${taskToPause.id}/status`, pausePayload);

            // Update local state and cache the previous status
            setTasks((prevTasks) => prevTasks.map((t) => (t.id === taskToPause.id ? { ...t, status: "Pause" } : t)));

            setTaskStatusHistory((prev) => ({
              ...prev,
              [taskToPause.id]: taskToPause.status,
            }));
          }
        }
      }

      // Regular status update
      await Http.patch(`/task/${taskId}/status`, { status: newStatus, comment: "" });

      // Update task status locally instead of refetching all data
      setTasks((prevTasks) => prevTasks.map((task) => (task.id === taskId ? { ...task, status: newStatus } : task)));

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Task status updated successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error updating task status:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to update task status",
        life: 3000,
      });
    }
  };

  // Helper functions for rendering
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const renderDeveloperAvatar = (developerId) => {
    if (!developerId) return null;
    const name = developers[developerId] || "User";
    const initials = name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);

    return <div className="avatar">{initials}</div>;
  };

  // Render label with color
  const renderLabel = (labelId) => {
    if (!labelId || !labels[labelId]) return null;
    const label = labels[labelId];

    return (
      <span
        className="badge"
        style={{
          backgroundColor: "transparent",
          color: label.color || "#ccc",
          border: `1px solid ${label.color || "#ccc"}`,
          padding: "3px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          display: "inline-block",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontWeight: "500",
          height: "24px",
          lineHeight: "18px",
          textAlign: "center",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        {label.name}
      </span>
    );
  };

  // Render task card
  const renderTaskCard = (task) => {
    const isHighlighted = highlightedItems[task.id] || false;
    const taskType = task.task_type_id && taskTypes[task.task_type_id] ? taskTypes[task.task_type_id] : null;
    const isDetailsExpanded = expandedDescriptions[task.id] || false;
    const canModifyTask = isUserAssignedToTask(task);
    const isPaused = task.status === "Pause";
    const previousStatus = isPaused ? getTaskPreviousStatus(task) : null;

    return (
      <div
        key={task.id}
        className={`task-card shadow ${isHighlighted ? "highlighted" : ""} ${draggingTask === task ? "dragging" : ""}`}
        style={{
          borderLeftColor: taskType?.color || "#4CAF50",
          cursor: isPaused ? "pointer" : canModifyTask ? "grab" : "pointer",
          backgroundColor: isHighlighted ? "#fff8e1" : "white",
          transition: "background-color 0.3s ease",
          opacity: canModifyTask ? 1 : 0.85,
          position: "relative", // Ensure relative positioning for absolute elements
          ...(isHighlighted && {
            animation: "pulse-highlight 2s ease",
          }),
        }}
        onClick={() => {
          setSelectedTask(task);
          setStatusUpdateVisible(true);
        }}
        draggable={canModifyTask && !isPaused}
        onDragStart={(e) => handleDragStart(e, task)}
        onDragEnd={handleDragEnd}
      >
        {!canModifyTask && (
          <div
            style={{
              position: "absolute",
              top: "2px",
              right: "2px",
              fontSize: "10px",
              color: "#6c757d",
            }}
          >
            <i className="pi pi-lock" title="You cannot modify this task's status"></i>
          </div>
        )}

        {/* Pause indicator - Simplified to just a small badge */}
        {isPaused && (
          <div
            style={{
              position: "absolute",
              top: "0",
              right: "0",
              backgroundColor: "#dc3545",
              color: "white",
              padding: "1px 4px",
              fontSize: "9px",
              fontWeight: "bold",
              borderRadius: "0 0 0 4px",
              zIndex: 1,
            }}
            title={`Paused from ${previousStatus || "previous status"}`}
          >
            PAUSED
          </div>
        )}

        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center">
            {/* Task Type Icon */}
            <i
              className={taskType?.icon || "pi pi-check-square"}
              style={{
                color: taskType?.color || "#4CAF50",
                fontSize: "16px",
                marginRight: "8px",
              }}
            />
            {/* Task Title */}
            <div style={{ fontWeight: "500", fontSize: "13px" }}>{task.title}</div>
          </div>
        </div>

        <div className="d-flex flex-column mb-2" style={{ fontSize: "11px", color: "#6c757d" }}>
          <div className="d-flex justify-content-between mb-1">
            {/* Planned Dates */}
            <div>
              <i className="pi pi-calendar" style={{ marginRight: "4px" }}></i>
              <span title="Planned Dates">
                Plan: {formatDate(task.start_date)} - {formatDate(task.end_date)}
              </span>
            </div>
            {/* Estimated Time */}
            {task.estimated_time && (
              <div>
                <i className="pi pi-clock" style={{ marginRight: "4px" }}></i>
                <span title="Estimated Time">{task.estimated_time}h</span>
              </div>
            )}
          </div>

          {/* Actual Dates */}
          {(task.actual_start_date || task.actual_end_date) && (
            <div className="mb-1">
              <i className="pi pi-calendar-times" style={{ marginRight: "4px" }}></i>
              <span title="Actual Dates">
                Actual: {task.actual_start_date ? formatDate(task.actual_start_date) : "N/A"} -{" "}
                {task.actual_end_date ? formatDate(task.actual_end_date) : "N/A"}
              </span>
            </div>
          )}

          {/* Show previous status for paused tasks */}
          {isPaused && previousStatus && (
            <div className="d-flex align-items-center mb-1" style={{ fontSize: "11px", color: "#dc3545" }}>
              <i className="pi pi-pause" style={{ marginRight: "4px" }}></i>
              <span>Paused from: {previousStatus}</span>
            </div>
          )}

          {/* Description Expansion */}
          {task.description && (
            <div
              className="mt-1"
              onClick={(e) => {
                e.stopPropagation();
                toggleDescriptionExpansion(task.id, e);
              }}
              style={{
                cursor: "pointer",
                color: isDetailsExpanded ? "#0747A6" : "#6c757d",
                textDecoration: "underline",
              }}
            >
              <i className={`pi ${isDetailsExpanded ? "pi-chevron-up" : "pi-chevron-down"}`} style={{ marginRight: "4px" }}></i>
              {isDetailsExpanded ? "Hide details" : "Show details"}
            </div>
          )}

          {/* Description */}
          {isDetailsExpanded && task.description && (
            <div
              className="mt-2 p-2"
              style={{
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                fontSize: "12px",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div dangerouslySetInnerHTML={{ __html: task.description }} style={{ maxWidth: "100%", overflowX: "auto" }} />
            </div>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: "11px", color: "#6c757d" }}>
          {/* Story Points */}
          <div>
            {task.points ? (
              <>
                <i className="pi pi-chart-bar" style={{ marginRight: "4px" }}></i>
                {task.points} points
              </>
            ) : (
              <>
                <i className="pi pi-chart-bar" style={{ marginRight: "4px" }}></i>
                No points
              </>
            )}
          </div>

          {/* Label and Developers */}
          <div className="d-flex align-items-center">
            {task.label_id && renderLabel(task.label_id)}
            <div className="ms-2">{task.developers && task.developers.length > 0 ? renderDevelopers(task.developers) : null}</div>
          </div>
        </div>
      </div>
    );
  };

  // Calculate story progress - Added from Backlog.jsx
  const calculateStoryProgress = (storyId) => {
    const storyTasks = tasks.filter((task) => task.story_id === storyId);

    if (storyTasks.length === 0) {
      return { percent: 0, inProgressCount: 0, doneCount: 0, totalCount: 0 };
    }

    const doneCount = storyTasks.filter((task) => task.status === "Done").length;
    const inProgressCount = storyTasks.filter((task) => task.status === "In Progress" || task.status === "Review").length;
    const totalCount = storyTasks.length;

    const percent = Math.round((doneCount / totalCount) * 100);

    return { percent, inProgressCount, doneCount, totalCount };
  };

  // Calculate total story points from all tasks
  const calculateTotalStoryPoints = (storyId) => {
    const storyTasks = tasks.filter((task) => task.story_id === storyId);
    if (storyTasks.length === 0) return 0;

    let totalPoints = 0;

    storyTasks.forEach((task) => {
      // Only use the task points, not the story points
      // First check for explicit task points property
      const pointValue = task.points || 0;

      // Convert to number if it's a string and add to total
      totalPoints += Number(pointValue) || 0;

      // For debugging
      if (pointValue) {
        console.log(`Task ${task.id} (${task.title}) has ${pointValue} points`);
      }
    });

    return totalPoints;
  };

  // Get all unique developers from tasks in a story
  const getStoryDevelopers = (storyId) => {
    const storyTasks = tasks.filter((task) => task.story_id === storyId);
    if (storyTasks.length === 0) return [];

    // Create a Set to store unique developer IDs
    const uniqueDeveloperIds = new Set();

    // Collect all developer IDs from tasks
    storyTasks.forEach((task) => {
      if (task.developers && Array.isArray(task.developers)) {
        task.developers.forEach((dev) => {
          if (dev && dev.id) {
            uniqueDeveloperIds.add(dev.id);
          }
        });
      }
    });

    // Convert Set to Array
    return Array.from(uniqueDeveloperIds);
  };

  // For debugging data structure
  useEffect(() => {
    if (tasks.length > 0) {
      // Log a sample task to console to check its structure
      const sampleTask = tasks.find((t) => t.story_id);
      if (sampleTask) {
        console.log("Sample task structure:", sampleTask);
      }
    }
  }, [tasks]);

  // Render progress bar for stories - Added from Backlog.jsx
  const renderProgressBar = (storyId) => {
    const storyTasks = tasks.filter((task) => task.story_id === storyId);

    if (storyTasks.length === 0) {
      return (
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <div style={{ fontSize: "11px", color: "#adb5bd", fontStyle: "italic" }}>No tasks</div>
        </div>
      );
    }

    // Group tasks by status
    const statusGroups = {
      "To Do": storyTasks.filter((task) => task.status === "To Do").length,
      "In Progress": storyTasks.filter((task) => task.status === "In Progress").length,
      Review: storyTasks.filter((task) => task.status === "Review").length,
      Done: storyTasks.filter((task) => task.status === "Done").length,
    };

    const totalCount = storyTasks.length;
    const doneCount = statusGroups["Done"];
    const percentDone = Math.round((doneCount / totalCount) * 100);

    // Calculate width for each section
    const widths = {
      Done: (statusGroups["Done"] / totalCount) * 100,
      Review: (statusGroups["Review"] / totalCount) * 100,
      "In Progress": (statusGroups["In Progress"] / totalCount) * 100,
      "To Do": (statusGroups["To Do"] / totalCount) * 100,
    };

    // Starting positions for each section
    const positions = {
      Done: 0,
      Review: widths["Done"],
      "In Progress": widths["Done"] + widths["Review"],
      "To Do": widths["Done"] + widths["Review"] + widths["In Progress"],
    };

    return (
      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <div style={{ fontSize: "11px", color: "#495057", marginRight: "8px", minWidth: "32px", fontWeight: "500" }}>
          {doneCount}/{totalCount}
        </div>
        <div
          style={{
            width: "100%",
            height: "6px",
            backgroundColor: "#f5f5f5",
            borderRadius: "3px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {widths["Done"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["Done"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["Done"]}%`,
                backgroundColor: "#198754",
                borderRadius: percentDone === 100 ? "3px" : "3px 0 0 3px",
                transition: "width 0.3s ease",
              }}
            ></div>
          )}
          {widths["Review"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["Review"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["Review"]}%`,
                backgroundColor: "#fd7e14",
                transition: "width 0.3s ease",
              }}
            ></div>
          )}
          {widths["In Progress"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["In Progress"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["In Progress"]}%`,
                backgroundColor: "#0d6efd",
                transition: "width 0.3s ease",
              }}
            ></div>
          )}
        </div>

        {percentDone === 100 ? (
          <div style={{ marginLeft: "8px", fontSize: "11px", color: "#198754" }}>
            <i className="pi pi-check" style={{ fontSize: "10px" }}></i>
          </div>
        ) : percentDone > 0 ? (
          <div style={{ marginLeft: "8px", fontSize: "11px", color: "#0d6efd" }}>{percentDone}%</div>
        ) : null}
      </div>
    );
  };

  // Render story row with tasks in respective status columns
  const renderStoryRow = (story) => {
    const storyTasks = getTasksForStory(story.id);
    const hasSubtasks = storyTasks.length > 0;
    const isExpanded = expandedStories[story.id] || false;
    const progress = calculateStoryProgress(story.id);
    const totalStoryPoints = calculateTotalStoryPoints(story.id);
    const storyDeveloperIds = getStoryDevelopers(story.id);

    return (
      <div key={story.id} className="story-row">
        <div style={{ width: "100%" }}>
          <div
            className="story-header"
            onClick={() => toggleStoryExpansion(story.id)}
            style={{
              display: "flex",
              padding: "10px 16px",
              borderRadius: "5px",
              cursor: "pointer",
              backgroundColor: "rgb(242 243 244)",
              borderLeft: `3px solid #0747A6`,
              borderBottom: "1px solid #e9ecef",
              marginBottom: "4px",
              alignItems: "center",
              transition: "background-color 0.15s ease",
            }}
          >
            <div style={{ width: "20px", marginRight: "8px" }}>
              {hasSubtasks ? (
                <i className={`pi ${isExpanded ? "pi-chevron-down" : "pi-chevron-right"}`} style={{ color: "#666" }} />
              ) : (
                <i className="pi pi-minus" style={{ color: "#ccc" }} />
              )}
            </div>

            <i
              className="pi pi-bookmark"
              style={{
                color: hasSubtasks ? "#0747A6" : "#97A0AF",
                fontSize: "14px",
                marginRight: "10px",
              }}
            />

            <div
              style={{
                fontWeight: "600",
                fontSize: "13px",
                flex: "0 0 auto",
                minWidth: "180px",
                maxWidth: "250px",
                color: "#343a40",
              }}
            >
              {story.title}
            </div>

            <div
              style={{
                fontSize: "12px",
                color: "#6c757d",
                marginLeft: "12px",
                display: "flex",
                alignItems: "center",
                flex: "0 0 auto",
                minWidth: "150px",
              }}
            >
              <i className="pi pi-folder" style={{ marginRight: "4px", fontSize: "12px" }}></i>
              <span style={{ maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {projects[story.project_id] || "N/A"}
              </span>

              {totalStoryPoints > 0 && (
                <span
                  style={{
                    marginLeft: "12px",
                    color: "#6c757d",
                    fontSize: "11px",
                  }}
                >
                  <i className="pi pi-chart-bar" style={{ marginRight: "2px", fontSize: "11px" }}></i>
                  Total points: {totalStoryPoints}
                </span>
              )}
            </div>

            <div style={{ flex: 1, marginLeft: "16px", marginRight: "8px" }}>{renderProgressBar(story.id)}</div>

            <div style={{ marginLeft: "auto", display: "flex", gap: "2px" }}>
              {storyDeveloperIds.length > 0
                ? storyDeveloperIds.map((devId) => renderDeveloperAvatar(devId))
                : story.developer_id && renderDeveloperAvatar(story.developer_id)}
            </div>

            {/* Create Task Button */}
            <button
              className="btn btn-sm btn-outline-primary ms-2"
              style={{ fontSize: "11px", padding: "2px 8px" }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent toggling story expansion
                setSelectedStory(story);
                setCreateTaskVisible(true);
              }}
            >
              <i className="pi pi-plus" style={{ fontSize: "10px", marginRight: "3px" }}></i>
              Create Task
            </button>
          </div>

          {isExpanded && hasSubtasks && (
            <div className="task-cells-container">
              {statuses.map((status) => {
                const tasksInStatus = getTasksByStatus(storyTasks, status);
                return (
                  <div
                    key={`${story.id}-${status}`}
                    className={`task-cell ${dragOverCell === status ? "drag-over" : ""}`}
                    style={{
                      backgroundColor: statusBackgrounds[status] || "#f8f9fa",
                    }}
                    onDragOver={(e) => handleDragOver(e, status)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, status)}
                  >
                    {tasksInStatus.length > 0 ? (
                      tasksInStatus.map((task) => renderTaskCard(task))
                    ) : (
                      <div style={{ padding: "8px", textAlign: "center", color: "#adb5bd", fontSize: "12px" }}>No tasks</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render standalone tasks section
  const renderStandaloneTasks = () => {
    const standaloneTasks = getStandaloneTasks();

    if (standaloneTasks.length === 0) {
      return null;
    }

    return (
      <div className="standalone-container story-row">
        <div style={{ width: "100%" }}>
          <div className="story-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <i
                className="pi pi-list"
                style={{
                  color: "#6c757d",
                  fontSize: "16px",
                  marginRight: "10px",
                }}
              />
              <div style={{ fontWeight: "600", fontSize: "14px" }}>Individual Tasks</div>
              <div style={{ fontSize: "12px", color: "#6c757d", marginLeft: "8px" }}>({standaloneTasks.length} tasks)</div>
            </div>

            {/* Create Individual Task Button */}
            <button
              className="btn btn-sm btn-outline-primary"
              style={{ fontSize: "11px", padding: "2px 8px" }}
              onClick={() => {
                setSelectedStory(null);
                setCreateTaskVisible(true);
              }}
            >
              <i className="pi pi-plus" style={{ fontSize: "10px", marginRight: "3px" }}></i>
              Create Task
            </button>
          </div>

          <div className="task-cells-container">
            {statuses.map((status) => {
              const tasksInStatus = getTasksByStatus(standaloneTasks, status);
              return (
                <div
                  key={`standalone-${status}`}
                  className={`task-cell ${dragOverCell === status ? "drag-over" : ""}`}
                  style={{
                    backgroundColor: statusBackgrounds[status] || "#f8f9fa",
                  }}
                  onDragOver={(e) => handleDragOver(e, status)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, status)}
                >
                  {tasksInStatus.length > 0 ? (
                    tasksInStatus.map((task) => renderTaskCard(task))
                  ) : (
                    <div style={{ padding: "8px", textAlign: "center", color: "#adb5bd", fontSize: "12px" }}>No tasks</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render kanban board
  const renderKanbanBoard = () => {
    // Count total tasks that match all filters
    const totalFilteredTasks = filteredTasks.length;
    // Count total tasks before filtering
    const totalTasks = tasks.length;
    // Show filter indicator if we're filtering tasks
    const isFiltering = totalFilteredTasks < totalTasks;

    return (
      <div className="kanban-board">
        {/* Visual indicator for active filters */}
        {isFiltering && (
          <div className="alert alert-info mb-3 p-2 d-flex align-items-center" style={{ borderRadius: "6px" }}>
            <i className="pi pi-filter me-2" style={{ fontSize: "14px" }}></i>
            <span style={{ fontSize: "13px" }}>
              Showing {totalFilteredTasks} of {totalTasks} tasks
              {developerFilter && (
                <span className="ms-2">
                  • Developer: <strong>{developers[developerFilter] || "Unknown"}</strong>
                </span>
              )}
              {projectFilter && (
                <span className="ms-2">
                  • Project: <strong>{projects[projectFilter] || "Unknown"}</strong>
                </span>
              )}
              {sprintFilter && sprints.find((s) => s.id === sprintFilter) && (
                <span className="ms-2">
                  • Sprint: <strong>{sprints.find((s) => s.id === sprintFilter).name}</strong>
                </span>
              )}
            </span>
            <button className="btn btn-sm btn-light ms-auto" style={{ fontSize: "11px", padding: "2px 6px" }} onClick={resetAllFilters}>
              <i className="pi pi-times me-1" style={{ fontSize: "10px" }}></i>
              Clear
            </button>
          </div>
        )}

        {/* Status headers */}
        <div className="kanban-headers">
          {statuses.map((status) => {
            // Only count tasks (both standalone and those within stories)
            const tasksInStatus = filteredTasks.filter((task) => task.status === status);
            return (
              <div key={status} className="kanban-header" style={{ backgroundColor: statusBackgrounds[status] }}>
                <span className="status-badge" style={{ backgroundColor: statusColors[status] }}>
                  {status}
                </span>
                <span className="ms-2">{tasksInStatus.length}</span>
              </div>
            );
          })}
        </div>

        {filteredStories.length === 0 && filteredTasks.length === 0 && sprintFilter ? (
          <div className="text-center p-5">
            <i className="pi pi-inbox" style={{ fontSize: "3rem", color: "#ccc", marginBottom: "1rem" }}></i>
            <h5>No Tasks in Sprint</h5>
            <p className="text-muted">There are no tasks in the selected sprint yet.</p>
          </div>
        ) : (
          <>
            {/* Story rows */}
            {filteredStories.map((story) => renderStoryRow(story))}

            {/* Standalone tasks */}
            {renderStandaloneTasks()}
          </>
        )}
      </div>
    );
  };

  // Handle adding a new task without refreshing all data
  const handleAddNewTask = (newTaskData) => {
    if (!newTaskData) return;

    // Add the newly created task to the tasks state
    setTasks((prevTasks) => {
      // Make sure we're not adding a duplicate
      const exists = prevTasks.some((task) => task.id === newTaskData.id);
      if (exists) return prevTasks;

      // Add developer data if it's not included in the newTaskData
      const taskWithDevelopers = {
        ...newTaskData,
        developers: Array.isArray(newTaskData.developers) ? newTaskData.developers : newTaskData.developer_ids?.map((id) => ({ id })) || [],
      };

      // Highlight the newly created task
      highlightItem(taskWithDevelopers.id);

      return [...prevTasks, taskWithDevelopers];
    });
  };

  // Handle updating a task without refreshing all data
  const handleUpdateTask = (updatedTaskData) => {
    if (!updatedTaskData) return;

    setTasks((prevTasks) => {
      return prevTasks.map((task) => {
        if (task.id === updatedTaskData.id) {
          // If we only received partial data (just id and status), merge it with existing task data
          const taskWithUpdates = {
            ...task, // Keep all existing task data
            ...updatedTaskData, // Override with any updated fields
            // Make sure developers are properly structured
            developers: Array.isArray(updatedTaskData.developers)
              ? updatedTaskData.developers
              : updatedTaskData.developer_ids?.map((id) => ({ id })) || task.developers || [],
          };

          // Highlight the updated task
          highlightItem(taskWithUpdates.id);

          return taskWithUpdates;
        }
        return task;
      });
    });
  };

  // Function to highlight an item for 2 seconds
  const highlightItem = (itemId) => {
    // Set the highlight
    setHighlightedItems((prev) => ({
      ...prev,
      [itemId]: true,
    }));

    // Remove the highlight after 2 seconds
    setTimeout(() => {
      setHighlightedItems((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    }, 2000);
  };

  // Render all developer avatars and names
  const renderDevelopers = (developers) => {
    if (!developers || developers.length === 0) return null;

    return (
      <div className="d-flex align-items-center">
        {developers.map((dev, index) => (
          <div
            key={dev.id}
            className="developer-badge d-flex align-items-center"
            style={{
              marginRight: index < developers.length - 1 ? "4px" : "0",
              whiteSpace: "nowrap",
              border: "1px solid #e9ecef",
              borderRadius: "12px",
              // padding: "2px 8px",
              fontSize: "11px",
              backgroundColor: "#f8f9fa",
            }}
          >
            {renderDeveloperAvatar(dev.id)}
          </div>
        ))}
      </div>
    );
  };

  // Main render function
  return (
    <div>
      <IsLogin />
      <Toast ref={toast} />
      <ConfirmDialog />
      <style>{kanbanBoardStyles}</style>

      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Project Board</h5>
          <div className="board-filters">
            <span className="p-input-icon-left" style={{ flex: 1, maxWidth: "250px" }}>
              <i className="pi pi-search" style={{ left: "8px", color: "#6c757d" }} />
              <InputText
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search board..."
                className="w-100"
                style={{ paddingLeft: "30px" }}
              />
            </span>

            <Dropdown
              value={projectFilter}
              options={projectOptions}
              onChange={(e) => setProjectFilter(e.value)}
              placeholder="Project"
              className="p-inputtext-sm"
            />

            <Dropdown
              value={sprintFilter}
              options={sprintOptions}
              onChange={(e) => handleSprintChange(e.value)}
              placeholder="Sprint"
              className="p-inputtext-sm"
            />

            <Dropdown
              value={developerFilter}
              options={developerOptions}
              onChange={(e) => setDeveloperFilter(e.value)}
              placeholder="Developer"
              className="p-inputtext-sm"
              optionLabel="label"
              itemTemplate={(option) => (
                <div className="d-flex align-items-center">
                  {option.value && (
                    <div
                      className="avatar me-2"
                      style={{
                        width: "20px",
                        height: "20px",
                        fontSize: "10px",
                      }}
                    >
                      {option.initials}
                    </div>
                  )}
                  <span>{option.label}</span>
                </div>
              )}
              valueTemplate={(option) => {
                if (!option || !option.value) return "Developer";
                return (
                  <div className="d-flex align-items-center">
                    <div
                      className="avatar me-2"
                      style={{
                        width: "18px",
                        height: "18px",
                        fontSize: "9px",
                      }}
                    >
                      {option.initials}
                    </div>
                    <span style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.label}</span>
                  </div>
                );
              }}
            />

            <Button
              icon="pi pi-times"
              className="p-button-text filter-clear"
              style={{ color: "white", border: "1px solid white" }}
              tooltip="Clear Filters"
              tooltipOptions={{ position: "top" }}
              onClick={resetAllFilters}
              disabled={!searchTerm && !projectFilter && !sprintFilter && !developerFilter}
            />

            <Button
              icon="pi pi-refresh"
              className="p-button-text filter-refresh"
              style={{ color: "white", border: "1px solid white" }}
              tooltip="Refresh Board"
              tooltipOptions={{ position: "top" }}
              onClick={() => {
                resetAllFilters();
                fetchData();
              }}
            />
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center p-5">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                <p className="mt-2">Loading project board...</p>
              </div>
            ) : (
              renderKanbanBoard()
            )}
          </div>
        </div>
      </div>

      {/* Dialog for Status Update */}
      <Dialog header="Update Task Status" visible={statusUpdateVisible} style={{ width: "500px" }} onHide={() => setStatusUpdateVisible(false)}>
        {selectedTask && (
          <>
            {!isUserAssignedToTask(selectedTask) ? (
              <div className="alert alert-warning">
                <i className="pi pi-exclamation-triangle mr-2"></i>
                You cannot change the status of this task because you are not assigned to it.
              </div>
            ) : selectedTask.status === "Pause" ? (
              <div>
                <div className="d-flex align-items-center mb-4">
                  <div
                    style={{
                      backgroundColor: "rgba(220, 53, 69, 0.1)",
                      borderRadius: "50%",
                      width: "56px",
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "16px",
                    }}
                  >
                    <i className="pi pi-pause" style={{ fontSize: "24px", color: "#dc3545" }}></i>
                  </div>
                  <div>
                    <h5 className="mb-1" style={{ fontWeight: "600" }}>
                      Paused Task
                    </h5>
                    <p className="mb-0 text-muted" style={{ fontSize: "14px" }}>
                      This task is currently paused
                    </p>
                  </div>
                </div>

                <div className="card mb-3" style={{ borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                  <div className="card-body p-3">
                    <div className="mb-3">
                      <div
                        className="text-muted mb-1"
                        style={{ fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}
                      >
                        Task Title
                      </div>
                      <div style={{ fontSize: "16px", fontWeight: "500" }}>{selectedTask.title}</div>
                    </div>

                    <div className="row">
                      <div className="col-6">
                        <div
                          className="text-muted mb-1"
                          style={{ fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}
                        >
                          Current Status
                        </div>
                        <div className="d-flex align-items-center">
                          <span
                            style={{
                              backgroundColor: "#dc3545",
                              color: "white",
                              padding: "3px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            Paused
                          </span>
                        </div>
                      </div>

                      <div className="col-6">
                        <div
                          className="text-muted mb-1"
                          style={{ fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}
                        >
                          Previous Status
                        </div>
                        <div className="d-flex align-items-center">
                          <span
                            style={{
                              backgroundColor: statusColors[getTaskPreviousStatus(selectedTask)] || "#6c757d",
                              color: "white",
                              padding: "3px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            {getTaskPreviousStatus(selectedTask)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="alert alert-light border mb-4" style={{ backgroundColor: "rgba(13, 110, 253, 0.05)", borderRadius: "8px" }}>
                  <div className="d-flex">
                    <i className="pi pi-info-circle" style={{ color: "#0d6efd", fontSize: "16px", marginRight: "12px", marginTop: "2px" }}></i>
                    <div>
                      <div style={{ fontWeight: "500", marginBottom: "4px" }}>Resuming this task</div>
                      <p className="mb-0" style={{ fontSize: "13px", color: "#495057" }}>
                        When you click Resume, this task will return to its previous status and any other active tasks will be automatically paused.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  className="btn btn-primary w-100 py-2"
                  style={{
                    backgroundColor: "#28a745",
                    borderColor: "#28a745",
                    fontWeight: "500",
                    boxShadow: "0 2px 4px rgba(40, 167, 69, 0.2)",
                    borderRadius: "6px",
                  }}
                  onClick={() => {
                    handleStatusChange(selectedTask.id, "Start");
                    setStatusUpdateVisible(false);
                  }}
                >
                  <i className="pi pi-play mr-2"></i> Resume Task
                </button>
              </div>
            ) : (
              <StatusUpdate
                taskId={selectedTask.id}
                currentStatus={selectedTask.status}
                validStatusTransitions={statuses.filter(
                  (status) => status === selectedTask.status || isValidStatusTransition(selectedTask.status, status)
                )}
                onSuccess={(updatedTaskData) => {
                  handleUpdateTask(updatedTaskData);
                  setStatusUpdateVisible(false);
                  toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Task status updated successfully",
                    life: 3000,
                  });
                }}
                setVisible={setStatusUpdateVisible}
              />
            )}
          </>
        )}
      </Dialog>

      {/* Dialog for Create Task */}
      <Dialog
        header="Create Task"
        visible={createTaskVisible}
        style={{ width: "80%", maxWidth: "900px" }}
        onHide={() => {
          setCreateTaskVisible(false);
          setSelectedStory(null);
        }}
      >
        <CreateTask
          initialStoryId={selectedStory?.id}
          onSuccess={(newTaskData) => {
            handleAddNewTask(newTaskData);
            setCreateTaskVisible(false);
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: "Task created successfully",
              life: 3000,
            });
          }}
        />
      </Dialog>

      {/* Add animation styles */}
      <style>
        {`
          @keyframes pulse-highlight {
            0% { background-color: #fff8e1; }
            50% { background-color: #ffecb3; }
            100% { background-color: #fff8e1; }
          }
        `}
      </style>
    </div>
  );
};

export default List;
